import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';

import { createProjectItem, updateProjectItem, deleteProjectItem, getProjectItems } from "../../api/projects";
import { Pagination } from "../common/Pagination";
import { applySortClick, DEFAULT_ITEM_SORT, isStoredItemSortKeys, ItemListQuery, LIST_DEFAULT_SIZE, SortParam, sortIndicator } from "../../types/pagination";
import { PAGE_SIZE_OPTIONS } from "../../constants/pagination";
import { hasStagedChanges } from "../common/stagedChanges";
import { SortControl } from "../common/SortControl";
import { clearStagedDraft, getStagedDraft, setStagedDraft } from "../../utils/stagedDrafts";
import { getStoredValue, storeValue } from "../../utils/localStorage";

const PROJECT_PLAN_PAGE_SIZE_KEY = 'projectPlan_pageSize';
const PROJECT_PLAN_SORT_KEY = 'projectPlan_sortKeys';

/** Sort fields offered in the sort panel. */
const PROJECT_PLAN_SORT_OPTIONS = [
  { value: 'created_at', label: 'Created' },
  { value: 'due_date', label: 'Due date' },
  { value: 'status', label: 'Status' },
  { value: 'risk', label: 'Risk' },
];
import { deleteProjectItemDocument } from "../../api/documents";


import { useProjectLocationsData } from '../../hooks/useProjectLocationsData';
import { useUsersPage } from "../../state/users";
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { LoadingSpinner } from '../LoadingSpinner';
import { TabProjectPlanDocumentAttachment } from './TabProjectPlanDocumentAttachment';
import { TaskAssetAttachment } from '../../components/TaskAssetAttachment';
import { PhaseSelectBadge } from './PhaseSelectBadge';
import { Avatar } from '../Avatar';
import { AddProjectItemModal } from './AddProjectItemModal';
import { PhaseManager } from './PhaseManager';
// import { ProjectItemAssets } from './ProjectItemAssets';


import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaMapMarkerAlt,
  FaTrash,
  FaThList,
  FaTh,
  FaLink
} from 'react-icons/fa';
import { KanbanBoard } from './KanbanBoard';

import { getTableHeaderStyle, getFormLabelStyle, getTaskNameStyle, getRiskColor, getFormSelectStyle, getFormSelectContainerStyle, getLocationSelectStyle, getOriginalStatusSelectStyle, getOriginalStatusColor, getBadgeTailwindClasses, DesignSystem } from '../../styles/designSystem';
import { RISK_OPTIONS, STATUS_LABELS } from '../../constants/project';
import { useCanEditTaskOwner } from '../../context/projectAccess';

interface TabProjectPlanProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  highlightedItemId?: string | null;
  initialRiskFilter?: string;
  readOnly?: boolean;
  archived?: boolean;
}

  // Task row component
  const TaskRow = ({
  project,
  projectData,
  onSubmit,
  onDelete,
  rooms,
  onProjectUpdate,
  isNew = false,
  isHighlighted = false,
  isAssetsEnabled = true,
  canDelete = false,
  archived = false,
}: {
  project: ProjectItemInterface,
  projectData: ProjectInterface,
  onSubmit: Function,
  onDelete: (projectItemId: string) => void,
  rooms: import('../../api/dataCenter').RoomLocation[],
  onProjectUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean,
  isAssetsEnabled?: boolean,
  canDelete?: boolean,
  archived?: boolean,
}) => {
  // Changing a task's owner requires tasks:update (FD2-1129)
  const canEditOwner = useCanEditTaskOwner();
  // Unsaved edits survive a tab switch, which unmounts the row; see utils/stagedDrafts.
  const draftKey = `project-item:${project.id}`;
  const [form, setForm] = useState<ProjectItemInterface>(() => getStagedDraft<ProjectItemInterface>(draftKey) ?? { ...project });
  // Last saved copy of the item. Edits are staged in `form` until Save; Cancel restores this copy.
  const [savedForm, setSavedForm] = useState({ ...project });
  const [isExpanded, setIsExpanded] = useState(false);
  const [fieldStatus, setFieldStatus] = useState<Record<string, 'saved' | 'saving' | 'unsaved' | 'idle'>>({});
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  // Asset loading moved to TaskAssetAttachment component


  const { addUserToProject } = useUsersPage();

  // Asset loading moved to TaskAssetAttachment component

  // A reload of this item must not discard edits the user has staged.
  const savedFormRef = useRef(savedForm);
  savedFormRef.current = savedForm;
  useEffect(() => {
    setSavedForm({ ...project });
    setForm(prev => (hasStagedChanges(prev, savedFormRef.current) ? prev : { ...project }));
    // Check date validation on data load
    if (project.start_date && project.due_date) {
      validateDates(project.start_date, project.due_date);
    }
    // Asset loading moved to TaskAssetAttachment component
  }, [project]);

  // Scroll to highlighted item
  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isHighlighted]);

  const handleCreatePhase = async (phaseId: string) => {
    console.log('Phase created with ID:', phaseId);
    handleFieldChange('phase_id', phaseId);
  };

  // Date validation function - returns null if valid, or error message string if invalid
  const validateDates = (startDate: string, dueDate: string): string | null => {
    if (!startDate || !dueDate) {
      setDateValidationError(null);
      return null;
    }

    const start = new Date(startDate);
    const due = new Date(dueDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(due.getTime())) {
      const error = 'Please enter valid dates';
      setDateValidationError(error);
      return error;
    }

    // Define reasonable date bounds (e.g., 10 years ago to 10 years in the future)
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
    const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());

    // Check if dates are within reasonable bounds
    if (start < minDate || start > maxDate || due < minDate || due > maxDate) {
      const error = 'Please enter valid dates within the last 10 years to the next 10 years';
      setDateValidationError(error);
      return error;
    }

    if (due < start) {
      const error = 'Due date cannot be earlier than start date';
      setDateValidationError(error);
      return error;
    }

    setDateValidationError(null);
    return null;
  };





  // Function to save changes
  const saveChanges = async (updatedForm: any, reloadAfterSave = false, field?: string) => {
    // Check date validation before saving
    const dateError = validateDates(updatedForm.start_date, updatedForm.due_date);
    if (dateError) {
      toast.error(dateError);
      return; // Don't save if dates are invalid
    }

    if (field) {
      setFieldStatus(prev => ({ ...prev, [field]: 'saving' }));
    }
    try {
      await onSubmit(updatedForm);
      setSavedForm({ ...updatedForm });
      // Reload all project data from API (like Documents tab does)
      if (reloadAfterSave && onProjectUpdate) {
        setTimeout(() => onProjectUpdate(), 500);
      }
      if (field) {
        setFieldStatus(prev => ({ ...prev, [field]: 'saved' }));
      }
    } finally {
    }
  };

  // Stages a change in the row. Nothing is sent to the backend until the user presses Save.
  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Blur stages the value; a blur without a change does not send a request.
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = {
      ...form,
      [field]: value
    };

    // Keep title and name in step, as one value
    if (field === 'title') {
      updatedForm.name = value;
    } else if (field === 'name') {
      updatedForm.title = value;
    }
    setForm(updatedForm);
  };

  // Any staged difference from the saved copy means unsaved changes.
  const isDirty = Object.keys({ ...form, ...savedForm }).some((key) =>
    JSON.stringify((form as Record<string, any>)[key] ?? null) !== JSON.stringify((savedForm as Record<string, any>)[key] ?? null)
  );

  const saveStagedChanges = () => saveChanges(form);

  const discardStagedChanges = () => {
    setForm({ ...savedForm });
  };

  // Keep the draft in step with the row: stored while edits are unsaved, cleared once they are saved or cancelled.
  useEffect(() => {
    if (isDirty) setStagedDraft(draftKey, form);
    else clearStagedDraft(draftKey);
  }, [isDirty, form, draftKey]);

  // Save immediately on Enter (Shift+Enter keeps newline)
  const handleEnterSave = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    transform?: (value: string) => any
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const rawValue = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
      const value = transform ? transform(rawValue) : rawValue;
      handleFieldBlur(field, value);
    }
  };

  const renderStatus = (field: string) => {
    const status = fieldStatus[field];
    if (status === 'saving') return 'Saving…';
    if (status === 'unsaved') return 'Unsaved changes';
    return null; // hide "Saved"/idle
  };


  const handleUserAdd = async (field: 'owner_id' | 'assigned_to', userId: string) => {
    const user = projectData?.users?.find(u => u.id === userId);
    if (user) {
      const userInProject = !!projectData.users?.find((u: ProjectUserInterface) => u.id === userId);
      if (!userInProject) {
        await addUserToProject(projectData.id || '', user);
        // FIX: Don't reload project to avoid conflicts
        // await loadProjectById(projectData.id || '');
      }
      handleFieldChange(field, userId);
      handleFieldBlur(field, userId);
    }
  };









  return (
    <div 
      ref={rowRef}
      data-testid="project-item-row"
      data-item-id={project.id}
      className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${
        isNew ? 'bg-[#f5f5f5]/30' : ''
      } ${
        isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
      } ${
        isDirty ? 'shadow-[inset_3px_0_0_#f59e0b]' : ''
      }`}
    >
      {isDirty && (
        <div data-testid="project-item-unsaved-bar" className="flex items-center justify-end gap-2 px-6 py-1.5 bg-amber-50 border-b border-amber-100">
          <span className="text-[11px] text-amber-700 mr-auto">Unsaved changes</span>
          <button
            data-testid="project-item-cancel"
            onClick={(e) => { e.stopPropagation(); discardStagedChanges(); }}
            className="px-2 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            data-testid="project-item-save"
            onClick={(e) => { e.stopPropagation(); saveStagedChanges(); }}
            className="px-2 py-1 text-xs font-medium text-white bg-[#171717] rounded hover:bg-[#333] transition-colors duration-150"
          >
            Save
          </button>
        </div>
      )}
      {/* Main Row */}
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Clickable Area for Expand */}
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />

        {/* Left: flexible name area */}
        <div className="flex items-center flex-1 min-w-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mr-4 p-1 rounded-md hover:bg-[#fafafa] transition-colors relative z-10"
          >
            <FaChevronDown className={`w-3 h-3 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          <div className="mr-4 relative z-10 flex items-center space-x-2">
            <div className="relative">
              {renderStatus('risk') && (
                <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin ${
                  form.risks?.toLowerCase() === 'high' ? 'border-t-red-500/50 border-r-red-500/50' :
                  form.risks?.toLowerCase() === 'medium' ? 'border-t-yellow-500/50 border-r-yellow-500/50' :
                  form.risks?.toLowerCase() === 'low' ? 'border-t-green-500/50 border-r-green-500/50' :
                  'border-t-gray-300/40 border-r-gray-300/40'
                }`} style={{ animation: 'spin 2.5s linear infinite' }} />
              )}
              <FaCircle
                className="w-2 h-2"
                style={{ color: getRiskColor(form.risks || '') }}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0 relative z-10 flex items-center">
            <input
              type="text"
              value={form.name || ''}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              onBlur={(e) => handleFieldBlur('name', e.target.value)}
              onKeyDown={(e) => handleEnterSave(e, 'name')}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              maxLength={255}
              className="flex-1 bg-transparent border-0 focus:outline-none placeholder-[#aaa]"
              style={getTaskNameStyle()}
              placeholder="Task name..."
            />
          </div>
        </div>

        {/* Right: fixed columns */}
        <div className="flex items-center flex-shrink-0 relative z-10">
          {/* Phase */}
          <div className="w-20 px-3">
            <PhaseSelectBadge
              phases={projectData?.phases || []}
              selectedPhaseId={form.phase_id || ''}
              projectId={project.project_id}
              onChange={(phaseId: string) => {
                handleFieldChange('phase_id', phaseId);
              }}
              onCreate={handleCreatePhase}
              onProjectUpdate={onProjectUpdate}
            />
          </div>

          {/* Status */}
          <div className="w-24 px-3">
            <select
              value={form.status || ''}
              onChange={(e) => {
                handleFieldChange('status', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              className={`${getOriginalStatusColor(form.status)}`}
              style={getOriginalStatusSelectStyle()}
            >
              <option value="NOT_STARTED">Not Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>

          {/* Owner */}
          <div className="w-11 px-1 flex justify-center" title={(() => {
            const user = projectData?.users?.find(u => u.id === form.owner_id);
            return user ? `Owner: ${user.first_name} ${user.last_name}` : 'Owner: Unassigned';
          })()}>
            <Avatar
              firstName={projectData?.users?.find(u => u.id === form.owner_id)?.first_name}
              lastName={projectData?.users?.find(u => u.id === form.owner_id)?.last_name}
              email={projectData?.users?.find(u => u.id === form.owner_id)?.email}
              size={28}
            />
          </div>

          {/* Due Date */}
          <div className="w-28 px-3 flex items-center text-xs text-[#555]">
            <input
              type="date"
              value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                handleFieldChange('due_date', value);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              min={(() => {
                const now = new Date();
                const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                return minDate.toISOString().split('T')[0];
              })()}
              max={(() => {
                const now = new Date();
                const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                return maxDate.toISOString().split('T')[0];
              })()}
              className="bg-transparent border-0 focus:outline-none text-xs w-full"
            />
          </div>

          {/* Created (read-only) */}
          <div className="w-28 px-3 flex items-center text-xs text-[#555]">
            {form.created_at ? new Date(form.created_at).toLocaleDateString() : ''}
          </div>

          {/* Progress */}
          <div className="w-20 px-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 bg-[#eaeaea] rounded-full h-1.5">
                <div
                  className="bg-[#171717] h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${form.completion_percentage || 0}%` }}
                ></div>
              </div>
              <span className="text-xs text-[#888]">{form.completion_percentage || 0}%</span>
            </div>
          </div>

          {/* Assets */}
          {isAssetsEnabled && !archived && (
            <div className="w-20 px-3">
              <TaskAssetAttachment
                projectId={project.project_id}
                itemId={project.id}
                onUpdate={onProjectUpdate}
              />
            </div>
          )}

          {/* Documents */}
          {!archived && (
          <div className="w-32 pl-3">
            <TabProjectPlanDocumentAttachment
              projectId={project.project_id}
              itemId={project.id}
              documentUrl={project.document}
              fileName={project.file_name}
              onDocumentChange={(docUrl, docName) => {
                // The document endpoint already stores the link, so no reload is needed. Only the local copies change.
                const documentFields = { document: docUrl || undefined, file_name: docName || undefined };
                setForm(prev => ({ ...prev, ...documentFields }));
                setSavedForm(prev => ({ ...prev, ...documentFields }));
              }}
            />
          </div>
          )}

          {/* Copy Link */}
          <div className="w-8 flex justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const url = `${window.location.origin}/projects/${projectData.id}?tab=project-plan-2&item=${project.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  toast.success('Link copied to clipboard');
                });
              }}
              className="p-1.5 rounded-md text-[#aaa] hover:text-[#171717] hover:bg-[#fafafa] opacity-0 group-hover:opacity-100 transition-all"
              title="Copy link to this task"
            >
              <FaLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 bg-[#fafafa]/30" style={{ borderTop: '0.5px solid #eaeaea' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Title and Description */}
            <div className="lg:col-span-2 space-y-4">
              {/* Title */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Title</label>
                <input
                  type="text"
                  value={form.title || form.name || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value, name: e.target.value }))}
                  onBlur={(e) => {
                    handleFieldBlur('title', e.target.value);
                  }}
                  onKeyDown={(e) => {
                    handleEnterSave(e, 'title');
                  }}
                  className="w-full bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-0 focus:border-[#888]"
                  style={getTaskNameStyle()}
                  placeholder={form.name || "Add title..."}
                />
                <div className="mt-1 text-[11px] text-[#aaa] text-right">
          {renderStatus('title')}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className={`w-full text-sm text-[#444] bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 resize-none ${
                    (form.description || '') !== (project.description || '')
                      ? 'border-amber-400 focus:ring-amber-200 focus:border-amber-500'
                      : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                  }`}
                  placeholder="Add description..."
                  rows={6}
                />
                {(form.description || '') !== (project.description || '') ? (
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      onClick={() => handleFieldBlur('description', form.description)}
                      className="px-3 py-1 text-xs font-medium text-white bg-[#171717] rounded-md hover:bg-[#333] transition-colors"
                    >
                      {fieldStatus['description'] === 'saving' ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, description: project.description || '' }))}
                      className="px-3 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors"
                    >
                      Discard
                    </button>
                    <span className="text-[11px] text-amber-600">Unsaved changes</span>
                  </div>
                ) : (
                  <div className="mt-1 text-[11px] text-[#aaa] text-right">
                    {renderStatus('description')}
                  </div>
                )}
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              {/* Location */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Location</label>
                <div className="flex items-center text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2">
                  <FaMapMarkerAlt className="w-3 h-3 mr-2 text-[#aaa]" />
                  <select
                    value={form.location?.room_id || ''}
                    onChange={(e) => {
                      const roomId = e.target.value;
                      handleFieldChange('location', roomId ? { room_id: roomId } : null);
                    }}
                    style={getLocationSelectStyle()}
                  >
                    <option value="">Select location</option>
                    {rooms.map((room) => (
                      <option key={room.room_id} value={room.room_id}>
                        {room.room_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Owner */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Owner</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.owner_id || ''}
                    onChange={(e) => handleUserAdd('owner_id', e.target.value)}
                    disabled={!canEditOwner}
                    title={!canEditOwner ? 'You do not have permission to change the owner' : undefined}
                    style={{
                      ...getFormSelectStyle(),
                      ...(canEditOwner ? {} : { backgroundColor: '#f5f5f5', cursor: 'not-allowed', opacity: 0.7 }),
                    }}
                  >
                    <option value="">Unassigned</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Assignee</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.assigned_to || ''}
                    onChange={(e) => handleUserAdd('assigned_to', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Unassigned</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Priority</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.priority || ''}
                    onChange={(e) => {
                      handleFieldChange('priority', e.target.value);
                    }}
                    style={getFormSelectStyle()}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2" style={getFormLabelStyle()}>Start Date</label>
                  <input
                    type="date"
                    value={form.start_date ? new Date(form.start_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      const updatedForm = { ...form, start_date: value };
                      setForm(updatedForm);

                      // Save only if validation passes successfully
                      if (!validateDates(value, form.due_date)) {
                        handleFieldChange('start_date', value);
                      }
                    }}
                    min={(() => {
                      const now = new Date();
                      const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                      return minDate.toISOString().split('T')[0];
                    })()}
                    max={(() => {
                      const now = new Date();
                      const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    className={`w-full text-sm text-[#444] bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
                      dateValidationError 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                    }`}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={getFormLabelStyle()}>Due Date</label>
                  <input
                    type="date"
                    value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      const updatedForm = { ...form, due_date: value };
                      setForm(updatedForm);

                      // Save only if validation passes successfully
                      if (!validateDates(form.start_date, value)) {
                        handleFieldChange('due_date', value);
                      }
                    }}
                    min={(() => {
                      const now = new Date();
                      const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                      return minDate.toISOString().split('T')[0];
                    })()}
                    max={(() => {
                      const now = new Date();
                      const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    className={`w-full text-sm text-[#444] bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
                      dateValidationError 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                    }`}
                  />
                </div>
              </div>

              {/* Date Validation Error */}
              {dateValidationError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-700 font-medium">{dateValidationError}</span>
                  </div>
                  <p className="text-xs text-red-600 mt-1 ml-6">
                    Please adjust the dates so that the due date is on or after the start date.
                  </p>
                </div>
              )}

              {/* Risk */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Risk Level</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.risks?.toLowerCase() || 'none'}
                    onChange={(e) => {
                      handleFieldChange('risks', e.target.value);
                    }}
                    style={getFormSelectStyle()}
                  >
                    {RISK_OPTIONS.map(option => (
                      <option key={option.value} value={option.value.toLowerCase()}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Progress */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Progress %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.completion_percentage || ''}
                  onChange={(e) => {
                    // Only update local state, don't save
                    const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                    setForm(prev => ({ ...prev, completion_percentage: value }));
                  }}
                  onBlur={(e) => {
                    // Save only on blur
                    const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                    handleFieldBlur('completion_percentage', value);
                  }}
                  className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-0 focus:border-[#888]"
                  placeholder="0"
                />
              </div>

              {/* Actions */}
              <div className="pt-2 flex items-center gap-2">
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/projects/${projectData.id}?tab=project-plan-2&item=${project.id}`;
                    navigator.clipboard.writeText(url).then(() => {
                      toast.success('Link copied to clipboard');
                    });
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-[#555] hover:text-[#171717] hover:bg-[#fafafa] rounded-lg transition-colors"
                >
                  <FaLink className="w-3 h-3" />
                  <span>Copy Link</span>
                </button>
                {canDelete && (
                <button
                  onClick={() => onDelete(project.id!)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Task</span>
                </button>
                )}
              </div>
            </div>
          </div>

          {/* Assets Section - Temporarily disabled until API is fixed */}
          {/*
          <div className="mt-6 pt-6 border-t border-[#eaeaea]">
            <ProjectItemAssets
              projectId={project.project_id}
              itemId={project.id}
              onUpdate={onProjectUpdate}
            />
          </div>
          */}
        </div>
      )}
    </div>
  );
};

export const TabProjectPlan = (
  { token, projectData, projectId, onProjectUpdate: onProjectUpdateProp, highlightedItemId, initialRiskFilter, readOnly: _readOnly, archived: _archived }: TabProjectPlanProps
) => {
  const { keycloak } = useKeycloak();
  // Every save or delete goes through here: reload the project, then refetch this tab's items and counts.
  const [itemsRefreshKey, setItemsRefreshKey] = useState(0);
  const onProjectUpdate = useCallback(async () => {
    await onProjectUpdateProp?.();
    setItemsRefreshKey((key) => key + 1);
  }, [onProjectUpdateProp]);
  const { handleAuthError } = useAuth();
  const { isFeatureEnabled } = useFeatureFlag();
  // Resolve current user ID from project users by email
  const currentUserId = useMemo(() => {
    const currentUser = projectData?.users?.find(user => user.email === keycloak.idTokenParsed?.email);
    return currentUser?.id || '';
  }, [projectData?.users, keycloak.idTokenParsed?.email]);

  // Add cleanup flag to prevent DOM operations after unmount. Must be a ref,
  // not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false`, permanently disabling every effect gated on it.
  const isUnmountedRef = useRef(false);
  const [newRows, setNewRows] = useState<ProjectItemInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>(initialRiskFilter || 'all');
  const [roomFilter, setRoomFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showPhaseManager, setShowPhaseManager] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Persist viewMode in localStorage
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(() => {
    const saved = localStorage.getItem(`projectPlan_viewMode_${projectId}`);
    return (saved === 'kanban' ? 'kanban' : 'table');
  });
  
  // Save viewMode to localStorage when it changes
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`projectPlan_viewMode_${projectId}`, viewMode);
    }
  }, [viewMode, projectId]);

  // Scroll detection
  useEffect(() => {
    if (isUnmountedRef.current) return;

    const handleScroll = () => {
      if (!isUnmountedRef.current) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup
  // too, so StrictMode's mount/cleanup/remount simulation doesn't leave this
  // permanently `true` after the initial mount.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Listen for document deletion events from Documents tab
  useEffect(() => {
    console.log('🎧 [TabProjectPlan] Setting up event listener for taskDocumentDeleted');
    console.log('🎧 [TabProjectPlan] Current projectId:', projectId);
    
    const handleDocumentDeleted = (event: CustomEvent) => {
      console.log('📢 [TabProjectPlan] Received taskDocumentDeleted event');
      console.log('📢 [TabProjectPlan] Event detail:', event.detail);
      console.log('📢 [TabProjectPlan] Current projectId:', projectId);
      console.log('📢 [TabProjectPlan] Event projectId:', event.detail.projectId);
      
      if (projectId && event.detail.projectId === projectId) {
        console.log('🔄 [TabProjectPlan] Project IDs match, will refresh after delay');
        // Add delay to allow backend to process the deletion and update relationships
        setTimeout(() => {
          console.log('🔄 [TabProjectPlan] Refreshing tasks now');
          if (onProjectUpdate) {
            onProjectUpdate();
          }
          console.log('✅ [TabProjectPlan] Refresh triggered');
        }, 2000); // 2 second delay
      } else {
        console.log('⚠️ [TabProjectPlan] Project IDs do not match or projectId is undefined');
      }
    };

    window.addEventListener('taskDocumentDeleted', handleDocumentDeleted as EventListener);
    console.log('✅ [TabProjectPlan] Event listener added');
    
    return () => {
      console.log('🧹 [TabProjectPlan] Removing event listener');
      window.removeEventListener('taskDocumentDeleted', handleDocumentDeleted as EventListener);
    };
  }, [projectId, onProjectUpdate]);

  // Load locations data (rooms, sites, floors) once at project level
  const { rooms } = useProjectLocationsData(projectId || '', _archived);

  // Tasks are searched, filtered, paged and counted by the backend. The page refetches when the
  // query changes, and when the project reloads (projectData changes after each save or delete).
  const [items, setItems] = useState<ProjectItemInterface[]>([]);
  const [itemsPage, setItemsPage] = useState(1);
  // Rows per page is remembered across visits, like the action-items tab.
  const [itemsSize, setItemsSizeState] = useState<number>(() =>
    getStoredValue(PROJECT_PLAN_PAGE_SIZE_KEY, (value): value is number => typeof value === 'number' && PAGE_SIZE_OPTIONS.includes(value), LIST_DEFAULT_SIZE)
  );
  const setItemsSize = (size: number) => {
    setItemsSizeState(size);
    storeValue(PROJECT_PLAN_PAGE_SIZE_KEY, size);
  };
  // Up to three sort keys, applied in order by the backend. Default: created_at:desc. Remembered across visits.
  const [sortKeys, setSortKeys] = useState<SortParam[]>(() =>
    getStoredValue(PROJECT_PLAN_SORT_KEY, isStoredItemSortKeys, DEFAULT_ITEM_SORT)
  );
  useEffect(() => {
    storeValue(PROJECT_PLAN_SORT_KEY, sortKeys);
  }, [sortKeys]);
  // Plain click: sort by this column (click again to flip). Shift-click: add it as a secondary key.
  const toggleSort = (field: 'status' | 'due_date' | 'created_at', additive: boolean) => {
    setSortKeys((keys) => applySortClick(keys, field, additive));
  };
  const [itemsPagination, setItemsPagination] = useState({ page: 1, size: LIST_DEFAULT_SIZE, total: 0 });
  const [itemCounts, setItemCounts] = useState<{ completed: number }>({ completed: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Any filter or size change starts again from the first page.
  useEffect(() => {
    setItemsPage(1);
  }, [debouncedSearch, statusFilter, userFilter, phaseFilter, riskFilter, roomFilter, itemsSize, sortKeys]);

  const itemQuery = useMemo<ItemListQuery>(() => ({
    page: itemsPage,
    size: itemsSize,
    search: debouncedSearch || undefined,
    statusValues: statusFilter !== 'all' ? [statusFilter] : undefined,
    assignedToIds: userFilter !== 'all' ? [userFilter] : undefined,
    phaseIds: phaseFilter !== 'all' ? [phaseFilter] : undefined,
    risk: riskFilter !== 'all' ? riskFilter : undefined,
    roomIds: roomFilter !== 'all' ? [roomFilter] : undefined,
    sort: sortKeys,
  }), [itemsPage, itemsSize, debouncedSearch, statusFilter, userFilter, phaseFilter, riskFilter, roomFilter, sortKeys]);

  // One request per filter, sort, page or size change.
  useEffect(() => {
    if (!token || !projectId) return;
    let cancelled = false;
    getProjectItems(token, projectId, itemQuery, { useCache: true })
      .then((page) => {
        if (cancelled) return;
        setItems(page.results ?? []);
        setItemsPagination({ page: page.page, size: page.size, total: page.total });
      })
      .catch((error: unknown) => {
        console.error('Failed to load project items:', error);
        toast.error('Failed to load project items');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, projectId, itemQuery, itemsRefreshKey]);

  // Completed count for the header. Refetched only after a reload, not on filter changes.
  useEffect(() => {
    if (!token || !projectId) return;
    let cancelled = false;
    getProjectItems(token, projectId, { size: 1, statusValues: ['COMPLETED'] }, { useCache: true })
      .then((completed) => {
        if (!cancelled) setItemCounts((counts) => ({ ...counts, completed: completed.total }));
      })
      .catch((error: unknown) => console.error('Failed to load completed item count:', error));
    return () => {
      cancelled = true;
    };
  }, [token, projectId, itemsRefreshKey]);

  const filteredTasks = items;

  const updateProjectItemHandler = async (form: ProjectItemInterface) => {
    console.log('updateProjectItemHandler called with:', form);
    if (token && projectId) {
      try {
        console.log('🔐 Token check:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          projectId: projectId
        });

        await updateProjectItem(token, projectId, form.id, form);
        console.log('updateProjectItem API call successful');
        
        // FIX: Remove project reload to prevent tasks from closing
        // if (onProjectUpdate) {
        //   console.log('Calling onProjectUpdate to refresh data...');
        //   await onProjectUpdate();
        //   console.log('onProjectUpdate completed');
        // }
        
        console.log('Project item saved successfully');
        toast.success('Project item updated successfully');
      } catch (error) {
        console.error('Error in updateProjectItemHandler:', error);
        
        // Check if this is an authentication error
        if (error instanceof Error && (error as any).isAuthError) {
          console.error('🔐 Authentication error detected in updateProjectItemHandler');
          toast.error('Your session has expired. Please refresh the page and try again.');
          
          // Redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
          return;
        }
        
        // Try to handle authentication error and retry
        const shouldRetry = await handleAuthError(error);
        if (shouldRetry && token && projectId) {
          console.log('🔄 Retrying updateProjectItem after token refresh...');
          try {
            await updateProjectItem(token, projectId, form.id, form);
            console.log('✅ Retry successful');
            toast.success('Project item updated successfully');
            return;
          } catch (retryError) {
            console.error('❌ Retry failed:', retryError);
            toast.error('Failed to save changes after retry');
            throw retryError;
          }
        }
        
        // Handle other errors
        if (error instanceof Error) {
          toast.error(error.message || 'Failed to save changes');
        } else {
          toast.error('Failed to save changes');
        }
        
        throw error; // Re-throw error for handleSave
      } finally {
        // Loading removed
      }
    } else {
      console.error('Missing token or projectId:', { token: !!token, projectId });
      toast.error('Authentication required. Please refresh the page.');
      throw new Error('Missing authentication or project ID');
    }
  }

  // Silent update for Kanban - no toast notifications
  const updateProjectItemSilent = async (form: ProjectItemInterface) => {
    if (token && projectId) {
      try {
        await updateProjectItem(token, projectId, form.id, form);
      } catch (error) {
        console.error('Error in silent update:', error);
        throw error;
      }
    }
  }

  const createProjectItemHandler = async (idx: number, form: ProjectItemInterface) => {
    if (!token || !projectId) return;
    
    try {
      await createProjectItem(token, projectId, form);
      console.log('createProjectItem API call successful');
      
      setNewRows(newRows.filter((_, i) => i !== idx));
      
      // Update project data to reflect changes
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
      
      console.log('New project item created successfully');
      toast.success('New project item created successfully');
    } catch (error: any) {
      console.error('Error in createProjectItemHandler:', error);
      
      // If 401 error, try to refresh token and retry once
      if (error.isAuthError || error.statusCode === 401) {
        console.log('🔄 Authentication error detected, refreshing token...');
        try {
          // Wait a moment for keycloak auto-refresh to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Get fresh token from keycloak
          const freshToken = keycloak?.token;
          
          if (freshToken) {
            console.log('🔄 Retrying with refreshed token...');
            await createProjectItem(freshToken, projectId, form);
            console.log('✅ createProjectItem successful after token refresh');
            
            setNewRows(newRows.filter((_, i) => i !== idx));
            
            // Update project data to reflect changes
            if (onProjectUpdate) {
              await onProjectUpdate();
            }
            
            console.log('New project item created successfully');
            toast.success('New project item created successfully');
            return;
          }
        } catch (retryError) {
          console.error('❌ Retry failed after token refresh:', retryError);
          toast.error('Session expired. Please refresh the page and try again.');
          return;
        }
      }
      
      toast.error('Failed to create new project item');
    }
  }

  const handleAddTask = async (itemData: any) => {
    if (!token || !projectId) return;
    
    try {
      console.log('Creating new task:', itemData);
      await createProjectItem(token, projectId, itemData);
      
      // Refresh project data
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
      
      console.log('Task created successfully');
      toast.success('Task created successfully');
    } catch (error: any) {
      console.error('Error creating task:', error);
      
      // If 401 error, try to refresh token and retry once
      if (error.isAuthError || error.statusCode === 401) {
        console.log('🔄 Authentication error detected, refreshing token...');
        try {
          // Wait a moment for keycloak auto-refresh to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Get fresh token from keycloak
          const freshToken = keycloak?.token;
          
          if (freshToken) {
            console.log('🔄 Retrying with refreshed token...');
            await createProjectItem(freshToken, projectId, itemData);
            
            // Refresh project data
            if (onProjectUpdate) {
              await onProjectUpdate();
            }
            
            console.log('✅ Task created successfully after token refresh');
            toast.success('Task created successfully');
            return;
          }
        } catch (retryError) {
          console.error('❌ Retry failed after token refresh:', retryError);
          toast.error('Session expired. Please refresh the page and try again.');
          throw retryError;
        }
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to create task';
      toast.error(errorMessage);
      throw error;
    }
  };

  const deleteProjectItemHandler = async (projectItemId: string) => {
    if (!token || !projectId) return;
    
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      // Find the task to check if it has a document
      const task = items.find((item: ProjectItemInterface) => item.id === projectItemId);
      
      // If task has a document, delete it first
      if (task?.document) {
        console.log(`Deleting document for task ${projectItemId} before deleting the task itself`);
        try {
          await deleteProjectItemDocument(token, projectId, projectItemId);
          console.log('Document deleted successfully');
        } catch (docError) {
          console.error('Failed to delete task document:', docError);
          // Continue with task deletion even if document deletion fails
          toast.warning('Task document could not be deleted, but will delete the task');
        }
      }
      
      // Delete the task itself
      await deleteProjectItem(token, projectId, projectItemId);
      toast.success('Task deleted successfully');
      
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      toast.error(error.message || 'Failed to delete task');
    }
  };



  const getStatusCount = (status: string) => {
    // Only COMPLETED is shown in the header; its count comes from the backend.
    return status === 'COMPLETED' ? itemCounts.completed : 0;
  };

  // Function to handle project update after phase changes
  const handlePhasesUpdate = async () => {
    if (onProjectUpdate) {
      await onProjectUpdate();
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
          {/* Search and Actions Row */}
          <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
          <div className="flex items-center justify-between">
            {/* Left side - Search and Quick Stats */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-80 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 focus:bg-white transition-all duration-150 placeholder-[#aaa]"
                />
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-[#888]">
                  {itemsPagination.total} tasks
                </span>
                <div className="w-px h-4 bg-[#ddd]"></div>
                <span className="text-[#888]">
                  {getStatusCount('COMPLETED')} completed
                </span>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-[#f5f5f5] rounded-lg p-0.5 border border-[#eaeaea]">
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'table'
                      ? 'bg-white text-[#171717] shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                      : 'text-[#888] hover:text-[#444]'
                  }`}
                  title="Table View"
                >
                  <FaThList className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Table</span>
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    viewMode === 'kanban'
                      ? 'bg-white text-[#171717] shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                      : 'text-[#888] hover:text-[#444]'
                  }`}
                  title="Kanban View"
                >
                  <FaTh className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Kanban</span>
                </button>
              </div>

              <SortControl
                keys={sortKeys}
                fields={PROJECT_PLAN_SORT_OPTIONS}
                onChange={setSortKeys}
                onReset={() => setSortKeys(DEFAULT_ITEM_SORT)}
              />
              {/* Filters */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  showFilters 
                    ? 'bg-[#171717] text-white border border-[#171717]'
                    : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
                }`}
              >
                <FaFilter className="w-3.5 h-3.5" />
                <span>Filter</span>
                {(statusFilter !== 'all' || userFilter !== 'all' || phaseFilter !== 'all' || riskFilter !== 'all' || roomFilter !== 'all') && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white text-[#171717] rounded-full font-semibold">
                    {[statusFilter, userFilter, phaseFilter, riskFilter, roomFilter].filter(f => f !== 'all').length}
                  </span>
                )}
              </button>

              {/* Manage Phases */}
              <button
                onClick={() => setShowPhaseManager(true)}
                className="flex items-center space-x-2 bg-white text-[#888] border border-[#eaeaea] px-3.5 py-2 rounded-lg text-[13px] font-medium hover:bg-[#fafafa] hover:border-[#ddd] transition-colors duration-150"
              >
                <FaPlus className="w-3.5 h-3.5" />
                <span>Manage Phases</span>
              </button>

              {/* Add Task */}
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors duration-150"
                >
                  <FaPlus className="w-3.5 h-3.5" />
                  <span>Add Task</span>
                </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #eaeaea' }}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Status</option>
                    <option value="NOT_STARTED">Not Started</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                      <option value="all">All Owners</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={phaseFilter}
                    onChange={(e) => setPhaseFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Phases</option>
                    {projectData?.phases?.map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={riskFilter}
                    onChange={(e) => setRiskFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Priority</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={roomFilter}
                    onChange={(e) => setRoomFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Locations</option>
                    {rooms?.map((room) => (
                      <option key={room.room_id} value={room.room_id}>
                        {room.room_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Active Filters & Clear */}
              {(statusFilter !== 'all' || userFilter !== 'all' || phaseFilter !== 'all' || riskFilter !== 'all' || roomFilter !== 'all') && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-[#888]">Active filters:</span>
                    {statusFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                        Status: {STATUS_LABELS[statusFilter] ?? statusFilter}
                        <button onClick={() => setStatusFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                      </span>
                    )}
                    {userFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                        User: {projectData?.users?.find(u => u.id === userFilter)?.first_name}
                        <button onClick={() => setUserFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setUserFilter('all');
                      setPhaseFilter('all');
                      setRiskFilter('all');
                      setRoomFilter('all');
                    }}
                    className="text-xs text-[#888] hover:text-[#444] font-medium"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

          {/* Table Header - Only show for table view */}
          {viewMode === 'table' && (
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                {/* Left: flexible name area */}
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-4 p-1"><div className="w-3"></div></div>
                  <div className="mr-4"><div className="w-2"></div></div>
                  <div className="flex-1" role="columnheader">Task</div>
                </div>
                {/* Right: fixed columns */}
                <div className="flex items-center flex-shrink-0">
                  <div className="w-20 px-3" role="columnheader">Phase</div>
                  <div className="w-24 px-3" role="columnheader">
                    <button
                      onClick={(event) => toggleSort('status', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Status&nbsp;{sortIndicator(sortKeys, 'status')}
                    </button>
                  </div>
                  <div className="w-11 px-1 text-center" role="columnheader">Owner</div>
                  <div className="w-28 px-3" role="columnheader">
                    <button
                      onClick={(event) => toggleSort('due_date', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Due&nbsp;{sortIndicator(sortKeys, 'due_date')}
                    </button>
                  </div>
                  <div className="w-28 px-3" role="columnheader">
                    <button
                      onClick={(event) => toggleSort('created_at', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Created&nbsp;{sortIndicator(sortKeys, 'created_at')}
                    </button>
                  </div>
                  <div className="w-20 px-3" role="columnheader">Progress</div>
                  {isFeatureEnabled('ASSETS_TAB') && (
                    <div className="w-20 px-3" role="columnheader">
                      Assets <span className={getBadgeTailwindClasses('dev')}>dev</span>
                    </div>
                  )}
                  <div className="w-32 pl-3" role="columnheader">Docs</div>
                  <div className="w-8"></div>
                </div>
              </div>
            </div>
          )}
          </div>
            </div>
          </div>

      {/* Content Area - Table or Kanban */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        {/* Loading State */}
        {isLoading && items.length === 0 && (
          <LoadingSpinner text="Loading project plan..." />
        )}

        {/* Table View */}
        {!isLoading && viewMode === 'table' && (
          <div className="bg-white">
            {/* Task Rows */}
            <div>
              {[...filteredTasks].sort((a: ProjectItemInterface, b: ProjectItemInterface) => a.id > b.id ? 1 : -1).map((project) => (
                <TaskRow
                  key={project.id}
                  project={project}
                  projectData={projectData}
                  onSubmit={updateProjectItemHandler}
                  onDelete={deleteProjectItemHandler}
                  rooms={rooms}
                  onProjectUpdate={onProjectUpdate}
                  isHighlighted={highlightedItemId === project.id}
                  isAssetsEnabled={isFeatureEnabled('ASSETS_TAB')}
                  canDelete={currentUserId === project.owner_id}
                  archived={_archived}
                />
              ))}
              
              {newRows.map((project: ProjectItemInterface, index) => (
                <TaskRow
                  key={index}
                  project={project}
                  projectData={projectData}
                  onSubmit={(form: ProjectItemInterface) => createProjectItemHandler(index, form)}
                  onDelete={deleteProjectItemHandler}
                  rooms={rooms}
                  onProjectUpdate={onProjectUpdate}
                  isNew={true}
                  isHighlighted={highlightedItemId === project.id}
                  isAssetsEnabled={isFeatureEnabled('ASSETS_TAB')}
                  archived={_archived}
                />
              ))}
            </div>
          </div>
        )}

        {/* Kanban View */}
        {!isLoading && viewMode === 'kanban' && projectId && (
          <div className="bg-[#fafafa]/30 rounded-xl py-4 mt-4">
            <KanbanBoard
              projectId={projectId}
              projectData={projectData}
              filteredTasks={filteredTasks}
              rooms={rooms}
              onUpdateTaskSilent={updateProjectItemSilent}
              onProjectUpdate={onProjectUpdate}
            />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && (
          <Pagination
            page={itemsPagination.page || itemsPage}
            size={itemsPagination.size || itemsSize}
            total={itemsPagination.total}
            onPageChange={setItemsPage}
            onSizeChange={setItemsSize}
          />
        )}

        {!isLoading && filteredTasks.length === 0 && newRows.length === 0 && (
          <div className="text-center py-16">
            <div className="text-[#aaa] mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-light text-[#171717] mb-2">No tasks found</h3>
            <p className="text-[#555] font-light mb-8">
              {searchQuery || statusFilter !== 'all' || userFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating your first task.'
              }
            </p>
              <button
                onClick={() => setShowAddTaskModal(true)}
                className="bg-white border border-[#eaeaea] text-[#555] px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
              >
                Create First Task
              </button>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {showAddTaskModal && projectId && (
        <AddProjectItemModal
          isOpen={showAddTaskModal}
          onClose={() => setShowAddTaskModal(false)}
          onAddItem={handleAddTask}
          projectData={projectData}
          users={projectData?.users || []}
        />
      )}

      {/* Phase Manager Modal */}
      {showPhaseManager && projectId && (
        <PhaseManager
          token={token}
          projectId={projectId}
          phases={projectData?.phases || []}
          onClose={() => setShowPhaseManager(false)}
          onPhasesUpdate={handlePhasesUpdate}
        />
      )}
    </div>
  );
}; 